import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Send, Loader2, Plus, Trash2, Power, Save, Bell, RefreshCw,
  GripVertical, Type, Image, ChevronUp, ChevronDown, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABELS = {
  no_shows: 'No Shows Yet',
  no_connections: 'No Connections Yet',
};

const TYPE_DESCRIPTIONS = {
  no_shows: 'Sent to users who have not added any shows.',
  no_connections: 'Sent to users with no follows AND no followers.',
};

const generateBlockId = () => `block_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const parseDescriptionToBlocks = (desc) => {
  if (!desc) return [{ id: generateBlockId(), type: 'text', content: '' }];
  const blocks = [];
  const parts = desc.split(/(\[IMAGE:[^\]]+\])/g);
  parts.forEach((part) => {
    if (!part) return;
    const m = part.match(/^\[IMAGE:(.+)\]$/);
    if (m) {
      blocks.push({ id: generateBlockId(), type: 'image', content: m[1] });
    } else {
      const trimmed = part.replace(/^\n+|\n+$/g, '');
      if (trimmed) blocks.push({ id: generateBlockId(), type: 'text', content: trimmed });
    }
  });
  if (blocks.length === 0) blocks.push({ id: generateBlockId(), type: 'text', content: '' });
  return blocks;
};

const serializeBlocksToDescription = (blocks) =>
  blocks
    .map((b) => (b.type === 'image' && b.content ? `[IMAGE:${b.content}]` : b.type === 'text' ? b.content : ''))
    .filter(Boolean)
    .join('\n\n');

const NudgesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eligibleCounts, setEligibleCounts] = useState({});
  const [togglingId, setTogglingId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [triggering, setTriggering] = useState(false);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [uploadingBlock, setUploadingBlock] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email/nudges/templates`, { withCredentials: true });
      const tpls = res.data.templates || [];
      setTemplates(tpls);
      // Fetch eligibility counts in parallel
      const counts = {};
      await Promise.all(tpls.map(async (t) => {
        try {
          const r = await axios.get(`${API}/admin/email/nudges/templates/${t.id}/recipients-count`, { withCredentials: true });
          counts[t.id] = r.data.eligible;
        } catch (_) { counts[t.id] = '—'; }
      }));
      setEligibleCounts(counts);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load nudge templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '',
      subject: t.subject || '',
      title: t.title || '',
      cta_text: t.cta_text || 'Open baad.tv',
      cta_url: t.cta_url || 'https://baad.tv',
      max_count: t.max_count ?? 3,
      interval_days: t.interval_days ?? 7,
      enabled: !!t.enabled,
    });
    setBlocks(parseDescriptionToBlocks(t.description));
  };

  const closeEdit = () => {
    setEditing(null); setForm(null); setBlocks([]);
  };

  const save = async () => {
    if (!form) return;
    const description = serializeBlocksToDescription(blocks).trim();
    if (!form.name || !form.subject || !form.title || !description) {
      toast.error('Fill in all required fields (and at least one content block)');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, description };
      const res = await axios.put(`${API}/admin/email/nudges/templates/${editing.id}`, payload, { withCredentials: true });
      setTemplates((prev) => prev.map((p) => (p.id === res.data.id ? res.data : p)));
      toast.success('Template saved');
      closeEdit();
      // Refetch eligibility (interval changed could shift count)
      try {
        const r = await axios.get(`${API}/admin/email/nudges/templates/${res.data.id}/recipients-count`, { withCredentials: true });
        setEligibleCounts((prev) => ({ ...prev, [res.data.id]: r.data.eligible }));
      } catch (_) {}
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join(', ') : d || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (t) => {
    setTogglingId(t.id);
    try {
      const res = await axios.post(`${API}/admin/email/nudges/templates/${t.id}/toggle`, {}, { withCredentials: true });
      setTemplates((prev) => prev.map((p) => (p.id === res.data.id ? res.data : p)));
      toast.success(`${res.data.name} ${res.data.enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toggle failed');
    } finally {
      setTogglingId(null);
    }
  };

  const sendTest = async (t) => {
    setTestingId(t.id);
    try {
      const res = await axios.post(`${API}/admin/email/nudges/templates/${t.id}/test`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Test email sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally {
      setTestingId(null);
    }
  };

  const triggerJob = async () => {
    if (!window.confirm("Run the Nudge Reminders job right now? It'll send to anyone currently eligible.")) return;
    setTriggering(true);
    try {
      const res = await axios.post(`${API}/admin/email-scheduler/trigger/nudges`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Nudge job triggered');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Trigger failed');
    } finally {
      setTriggering(false);
    }
  };

  // ----- Block handlers (local to this modal) -----
  const addBlock = (type) => setBlocks((p) => [...p, { id: generateBlockId(), type, content: '' }]);
  const updateBlock = (id, content) => setBlocks((p) => p.map((b) => (b.id === id ? { ...b, content } : b)));
  const removeBlock = (id) => setBlocks((p) => (p.length <= 1 ? (toast.error('At least one block required') || p) : p.filter((b) => b.id !== id)));
  const moveBlock = (idx, dir) => setBlocks((p) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= p.length) return p;
    const next = [...p]; const [r] = next.splice(idx, 1); next.splice(ni, 0, r); return next;
  });
  const onDragStart = (e, i) => { setDraggedIndex(i); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === i) return;
    setBlocks((p) => { const n = [...p]; const [r] = n.splice(draggedIndex, 1); n.splice(i, 0, r); return n; });
    setDraggedIndex(i);
  };
  const onDragEnd = () => setDraggedIndex(null);
  const uploadImage = async (blockId, file) => {
    if (!file) return;
    const ok = ['image/jpeg','image/png','image/gif','image/webp'];
    if (!ok.includes(file.type)) return toast.error('JPEG / PNG / GIF / WebP only');
    if (file.size > 5 * 1024 * 1024) return toast.error('Max 5MB');
    setUploadingBlock(blockId);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/admin/email/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.success) {
        updateBlock(blockId, r.data.url);
        toast.success('Image uploaded');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingBlock(null);
    }
  };

  return (
    <div className="onboarding-tab" data-testid="nudges-tab">
      <Card className="onboarding-card">
        <CardHeader>
          <div className="onboarding-header-row">
            <div>
              <CardTitle><Bell size={20} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Nudge Reminders</CardTitle>
              <CardDescription>
                Recurring reminders for users who haven&apos;t added shows or made connections.
                Runs weekly (Wed 9 AM UTC); each user gets up to <em>max_count</em> sends, then stops automatically.
              </CardDescription>
            </div>
            <Button onClick={triggerJob} disabled={triggering} variant="outline" className="create-template-btn">
              {triggering ? <Loader2 size={14} className="spinning" /> : <RefreshCw size={14} />}
              Run job now
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="loading-state"><Loader2 className="spinning" size={24} /> Loading nudge templates…</div>
          ) : (
            <div className="templates-grid">
              {templates.map((t) => (
                <Card key={t.id} className={`template-card ${t.enabled ? 'enabled' : 'disabled'}`} data-testid={`nudge-card-${t.type}`}>
                  <CardHeader>
                    <div className="template-header">
                      <div className="template-info">
                        <CardTitle>{t.name}</CardTitle>
                        <div className="template-meta" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.3rem' }}>
                          <span className="days-badge">{TYPE_LABELS[t.type] || t.type}</span>
                          <span className="days-badge">Every {t.interval_days}d · max {t.max_count}</span>
                          <span className={`status-badge ${t.enabled ? 'enabled' : 'disabled'}`}>
                            {t.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <p style={{ color: '#888', fontSize: '0.8rem', margin: '0.4rem 0 0' }}>
                          {TYPE_DESCRIPTIONS[t.type]}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="template-content">
                      <div className="template-subject"><strong>Subject:</strong> {t.subject}</div>
                      <div className="template-title"><strong>Title:</strong> {t.title}</div>
                      <div style={{ color: '#aaa', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                        <strong>Eligible right now:</strong>{' '}
                        <span data-testid={`nudge-eligible-${t.type}`}>{eligibleCounts[t.id] ?? '…'}</span>
                        <span style={{ marginLeft: '1rem' }}><strong>Sent total:</strong> {t.sent_count || 0}</span>
                      </div>
                      <div className="template-actions">
                        <Button onClick={() => openEdit(t)} variant="outline" size="sm" data-testid={`nudge-edit-${t.type}`}>
                          <Save size={14} /> Edit
                        </Button>
                        <Button onClick={() => toggleEnabled(t)} variant="outline" size="sm" disabled={togglingId === t.id}>
                          {togglingId === t.id ? <Loader2 size={14} className="spinning" /> : <Power size={14} />}
                          {t.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        <Button onClick={() => sendTest(t)} variant="outline" size="sm" disabled={testingId === t.id}>
                          {testingId === t.id ? <Loader2 size={14} className="spinning" /> : <Send size={14} />}
                          Test
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editing && form && (
        <div className="modal-overlay" onClick={closeEdit}>
          <div className="modal-content onboarding-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Nudge: {TYPE_LABELS[editing.type] || editing.type}</h2>
              <button className="close-btn" onClick={closeEdit}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Internal Name *</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Subject Line *</label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Email Title *</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>

              <div className="form-group">
                <label>Content *</label>
                <p className="field-hint" style={{ color: '#999', fontSize: '0.8rem', margin: '0.25rem 0 0.5rem' }}>
                  Drag to reorder. Add text and image blocks.
                </p>
                <div className="content-blocks">
                  {blocks.map((block, index) => (
                    <div
                      key={block.id}
                      className={`content-block ${block.type} ${draggedIndex === index ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, index)}
                      onDragOver={(e) => onDragOver(e, index)}
                      onDragEnd={onDragEnd}
                    >
                      <div className="block-header">
                        <div className="block-drag-handle"><GripVertical size={16} /></div>
                        <div className="block-type-label">
                          {block.type === 'text' && <><Type size={14} /> Text Block</>}
                          {block.type === 'image' && <><Image size={14} /> Image</>}
                        </div>
                        <div className="block-controls">
                          <button className="block-move-btn" onClick={() => moveBlock(index, -1)} disabled={index === 0}><ChevronUp size={16} /></button>
                          <button className="block-move-btn" onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1}><ChevronDown size={16} /></button>
                          <button className="block-remove-btn" onClick={() => removeBlock(block.id)}><Trash2 size={16} /></button>
                        </div>
                      </div>
                      <div className="block-content">
                        {block.type === 'text' && (
                          <textarea
                            value={block.content}
                            onChange={(e) => updateBlock(block.id, e.target.value)}
                            placeholder={"Enter your text here..."}
                            rows={4}
                            className="block-textarea"
                          />
                        )}
                        {block.type === 'image' && (
                          <div className="image-block-input">
                            <div className="image-upload-options">
                              <label className="upload-btn">
                                {uploadingBlock === block.id ? <Loader2 size={16} className="spinning" /> : <Plus size={16} />}
                                {uploadingBlock === block.id ? 'Uploading...' : 'Upload Image'}
                                <input type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                                  onChange={(e) => uploadImage(block.id, e.target.files[0])}
                                  disabled={uploadingBlock === block.id} style={{ display: 'none' }} />
                              </label>
                              <span className="or-divider">or</span>
                              <Input value={block.content} onChange={(e) => updateBlock(block.id, e.target.value)}
                                placeholder="Paste image URL" className="url-input" />
                            </div>
                            {block.content && (
                              <div className="image-preview">
                                <img src={block.content} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
                                <button className="remove-image-btn" onClick={() => updateBlock(block.id, '')}><Trash2 size={14} /></button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="add-block-buttons">
                  <Button variant="outline" onClick={() => addBlock('text')} className="add-block-btn">
                    <Plus size={16} /> <Type size={16} /> Add Text
                  </Button>
                  <Button variant="outline" onClick={() => addBlock('image')} className="add-block-btn">
                    <Plus size={16} /> <Image size={16} /> Add Image
                  </Button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>CTA Button Text</label>
                  <Input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>CTA URL</label>
                  <Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Max sends per user</label>
                  <Input type="number" min="1" max="20" value={form.max_count}
                    onChange={(e) => setForm({ ...form, max_count: parseInt(e.target.value, 10) || 1 })} />
                </div>
                <div className="form-group">
                  <label>Days between sends</label>
                  <Input type="number" min="1" max="90" value={form.interval_days}
                    onChange={(e) => setForm({ ...form, interval_days: parseInt(e.target.value, 10) || 7 })} />
                </div>
              </div>

              <label style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center', color: '#ccc' }}>
                <input type="checkbox" checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                Enabled
              </label>
            </div>
            <div className="modal-footer">
              <Button variant="outline" onClick={closeEdit}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="create-template-btn">
                {saving ? <><Loader2 size={16} className="spinning" /> Saving…</> : <><Save size={16} /> Save</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NudgesTab;
